import type {
  AgentSessionPermissionProfile,
  AgentWorkbenchComposerInputAction,
  AgentWorkbenchComposerModelOption,
  AgentWorkbenchComposerViewModel,
} from "@inf-monkeys-tech/monkeys";
import {
  ArrowDown,
  ArrowUp,
  CircleAlert,
  File,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  Mic,
  Paperclip,
  Pencil,
  RotateCcw,
  Send,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useMonkeysLocaleMessages } from "../../provider";
import { cn } from "../layout";
import {
  AutosizeTextarea,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../primitives";
import {
  type AgentWorkbenchAppearanceOptions,
  useAgentWorkbenchAppearance,
} from "./appearance";

interface ComposerSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ComposerSelectProps {
  value: string;
  label: string;
  placeholder?: string;
  options: readonly ComposerSelectOption[];
  className?: string;
  onValueChange: (value: string) => void;
}

function ComposerSelect({
  value,
  label,
  placeholder,
  options,
  className,
  onValueChange,
}: ComposerSelectProps) {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Select
      value={value || undefined}
      disabled={options.length === 0}
      onValueChange={onValueChange}
    >
      <SelectTrigger
        size="sm"
        aria-label={label}
        className={cn("shrink bg-background font-medium", className)}
      >
        <SelectValue placeholder={placeholder}>
          {selectedOption?.label}
        </SelectValue>
      </SelectTrigger>
      <SelectContent disableTriggerViewportSizing>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export type AgentWorkbenchComposerIntent =
  | { type: "change-value"; value: string }
  | { type: "submit" }
  | { type: "stop" }
  | { type: "request-attachments"; files: File[] }
  | { type: "select-input-action"; actionId: string }
  | { type: "request-voice-input" }
  | { type: "retry-attachment"; attachmentId: string }
  | { type: "remove-attachment"; attachmentId: string }
  | { type: "change-mode"; mode: "chat" | "work" }
  | { type: "select-model"; modelId: string; mode: "chat" | "work" }
  | { type: "change-reasoning"; value: "low" | "medium" | "high" | "xhigh" }
  | { type: "change-permission"; value: AgentSessionPermissionProfile }
  | { type: "toggle-web-search"; enabled: boolean }
  | { type: "edit-queued-draft"; draftId: string; text: string }
  | { type: "delete-queued-draft"; draftId: string }
  | { type: "move-queued-draft"; draftId: string; direction: "up" | "down" };

export interface AgentWorkbenchComposerClassNames {
  root?: string;
  queue?: string;
  attachments?: string;
  textarea?: string;
  toolbar?: string;
}

export interface AgentWorkbenchComposerProps
  extends AgentWorkbenchAppearanceOptions {
  viewModel: AgentWorkbenchComposerViewModel;
  disabled?: boolean;
  modeSelectionEnabled?: boolean;
  modeFilteringEnabled?: boolean;
  className?: string;
  classNames?: AgentWorkbenchComposerClassNames;
  onIntent?: (intent: AgentWorkbenchComposerIntent) => void;
}

export type ComposerInputAction = AgentWorkbenchComposerInputAction;

function modelOptionValue(option: AgentWorkbenchComposerModelOption): string {
  return `${option.mode}:${option.id}`;
}

export function AgentWorkbenchComposer({
  viewModel,
  disabled = false,
  modeSelectionEnabled = false,
  modeFilteringEnabled = false,
  variant,
  density,
  className,
  classNames,
  onIntent: emitIntent,
}: AgentWorkbenchComposerProps) {
  const current = useRef({ disabled, emitIntent }); current.current = { disabled, emitIntent };
  const onIntent = (intent: AgentWorkbenchComposerIntent) => { if (!current.current.disabled) current.current.emitIntent?.(intent); };
  const appearance = useAgentWorkbenchAppearance({ variant, density });
  const compact = appearance.density === "compact";
  const iconButtonClass = cn(
    compact ? "size-7" : "size-8",
    "shrink-0 p-0 text-muted-foreground",
  );
  const messages = useMonkeysLocaleMessages().agentWorkbench;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [editingDraftId, setEditingDraftId] = useState<string>();
  const [editingText, setEditingText] = useState("");
  const [isNarrow, setIsNarrow] = useState(false);
  const narrowBreakpoint = modeSelectionEnabled ? 900 : 704;
  const isBusy =
    viewModel.status === "loading" ||
    viewModel.status === "streaming" ||
    viewModel.status === "stopping";
  const isStopping = viewModel.status === "stopping";
  const isUploading = viewModel.attachments.some(
    (attachment) => attachment.status === "uploading",
  );
  const canSubmit =
    !disabled &&
    !isStopping &&
    !isUploading &&
    (viewModel.value.trim().length > 0 ||
      viewModel.attachments.some((item) => item.status === "ready"));
  const showSubmitButton = !isBusy || canSubmit;
  const filterModelsByMode = modeSelectionEnabled || modeFilteringEnabled;
  const visibleModelOptions = useMemo(
    () =>
      filterModelsByMode
        ? viewModel.model.options.filter(
            (option) => option.mode === viewModel.mode,
          )
        : viewModel.model.options,
    [filterModelsByMode, viewModel.mode, viewModel.model.options],
  );
  const selectedModelValue = useMemo(() => {
    const selected = visibleModelOptions.find(
      (option) =>
        option.id === viewModel.model.selectedId &&
        option.mode === viewModel.mode,
    );
    return selected
      ? modelOptionValue(selected)
      : visibleModelOptions[0]
        ? modelOptionValue(visibleModelOptions[0])
        : "";
  }, [viewModel.mode, viewModel.model.selectedId, visibleModelOptions]);
  const inputActions = viewModel.inputActions || [];
  const selectedInputActionId = viewModel.selectedInputActionId;

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return undefined;

    const updateLayout = () =>
      setIsNarrow(surface.getBoundingClientRect().width <= narrowBreakpoint);
    updateLayout();
    window.addEventListener('resize', updateLayout);
    if (typeof ResizeObserver === "undefined") {
      const interval = window.setInterval(updateLayout, 250);
      return () => {
        window.clearInterval(interval);
        window.removeEventListener("resize", updateLayout);
      };
    }

    const observer = new ResizeObserver(updateLayout);
    observer.observe(surface);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateLayout);
    };
  }, [narrowBreakpoint]);

  const submit = () => {
    if (canSubmit) onIntent?.({ type: "submit" });
  };

  return (
    <fieldset disabled={disabled} className="min-w-0 border-0 p-0">
    <form
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      data-agent-workbench-status={viewModel.status}
      className={cn(
        "agent-workbench-composer flex min-w-0 w-full flex-col",
        compact ? "gap-1.5" : "gap-2",
        className,
        classNames?.root,
      )}
      aria-label={messages.composer.message}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {viewModel.capabilities.queue && viewModel.queuedDrafts.length > 0 ? (
        <section
          aria-label={messages.composer.queued}
          className={cn(
            "min-w-0 max-w-full rounded-md border border-border bg-muted/40 p-2",
            classNames?.queue,
          )}
        >
          <ol className="space-y-1.5">
            {viewModel.queuedDrafts.map((draft, index) => (
              <li
                key={draft.id}
                className="flex min-h-9 min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-md bg-background px-2 py-1.5 text-sm"
              >
                <span className="w-5 shrink-0 text-center text-xs text-muted-foreground">
                  {index + 1}
                </span>
                {editingDraftId === draft.id ? (
                  <Input
                    autoFocus
                    value={editingText}
                    aria-label={messages.composer.editQueued}
                    appearance="compact"
                    className="h-8 min-w-0 flex-1 px-2 py-1"
                    onChange={(event) => setEditingText(event.target.value)}
                    onBlur={() => {
                      if (editingText.trim())
                        onIntent?.({
                          type: "edit-queued-draft",
                          draftId: draft.id,
                          text: editingText,
                        });
                      setEditingDraftId(undefined);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setEditingDraftId(undefined);
                      if (event.key === "Enter" && editingText.trim()) {
                        event.preventDefault();
                        onIntent?.({
                          type: "edit-queued-draft",
                          draftId: draft.id,
                          text: editingText,
                        });
                        setEditingDraftId(undefined);
                      }
                    }}
                  />
                ) : (
                  <span className="min-w-0 flex-1 truncate">
                    {draft.text || messages.composer.attachmentOnly}
                  </span>
                )}
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={iconButtonClass}
                    aria-label={messages.composer.moveUp}
                    disabled={index === 0}
                    onClick={() =>
                      onIntent?.({
                        type: "move-queued-draft",
                        draftId: draft.id,
                        direction: "up",
                      })
                    }
                  >
                    <ArrowUp className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={iconButtonClass}
                    aria-label={messages.composer.moveDown}
                    disabled={index === viewModel.queuedDrafts.length - 1}
                    onClick={() =>
                      onIntent?.({
                        type: "move-queued-draft",
                        draftId: draft.id,
                        direction: "down",
                      })
                    }
                  >
                    <ArrowDown className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={iconButtonClass}
                    aria-label={messages.composer.editQueued}
                    onClick={() => {
                      setEditingDraftId(draft.id);
                      setEditingText(draft.text);
                    }}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      iconButtonClass,
                      "text-destructive-text hover:text-destructive-text",
                    )}
                    aria-label={messages.composer.deleteQueued}
                    onClick={() =>
                      onIntent?.({
                        type: "delete-queued-draft",
                        draftId: draft.id,
                      })
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <div
        ref={surfaceRef}
        data-agent-workbench-surface="elevated"
        className={cn(
          "agent-workbench-composer__surface min-w-0 max-w-full rounded-[var(--radius)] border border-input bg-popover text-popover-foreground shadow-sm transition-[border-color,box-shadow] focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30",
          compact ? "p-2.5" : "p-3",
        )}
      >
        {viewModel.attachments.length > 0 ? (
          <div
            data-agent-workbench-composer-attachments
            className={cn(
              "mb-2 flex max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1",
              classNames?.attachments,
            )}
          >
            {viewModel.attachments.map((attachment) => {
              const isAttachmentError = attachment.status === "error";
              const isAttachmentUploading = attachment.status === "uploading";
              return (
                <div
                  key={attachment.id}
                  role={isAttachmentError ? 'alert' : undefined}
                  className={cn(
                    "flex h-14 min-w-48 max-w-64 shrink-0 items-center gap-2 rounded-md border bg-muted/45 p-1.5",
                    isAttachmentError
                      ? "border-destructive/50 bg-destructive/5"
                      : "border-border",
                  )}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
                    {attachment.previewUrl &&
                    attachment.mediaType?.startsWith("image/") ? (
                      <img
                        src={attachment.previewUrl}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : attachment.mediaType?.startsWith("image/") ? (
                      <ImageIcon className="size-5 text-muted-foreground" />
                    ) : (
                      <File className="size-5 text-muted-foreground" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-xs font-medium text-foreground"
                      title={attachment.name}
                    >
                      {attachment.name}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 flex items-center gap-1 text-[11px]",
                        isAttachmentError
                          ? "text-destructive-text"
                          : "text-muted-foreground",
                      )}
                    >
                      {isAttachmentUploading ? (
                        <LoaderCircle
                          className="size-3 animate-spin"
                          aria-hidden="true"
                        />
                      ) : null}
                      {isAttachmentError ? (
                        <CircleAlert className="size-3" aria-hidden="true" />
                      ) : null}
                      {isAttachmentUploading
                        ? messages.composer.uploading
                        : isAttachmentError
                          ? messages.composer.uploadFailed
                          : attachment.mediaType ||
                            messages.composer.attachmentOnly}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-0.5">
                    {isAttachmentError ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="!size-7 min-w-7 shrink-0 p-0 text-destructive-text hover:text-destructive-text"
                        aria-label={`${messages.composer.retryAttachment}: ${attachment.name}`}
                        onClick={() =>
                          onIntent?.({
                        type: 'retry-attachment',
                            attachmentId: attachment.id,
                          })
                        }
                      >
                        <RotateCcw className="size-3.5" />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="!size-7 min-w-7 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                      aria-label={`${messages.composer.removeAttachment}: ${attachment.name}`}
                      onClick={() =>
                        onIntent?.({
                          type: "remove-attachment",
                          attachmentId: attachment.id,
                        })
                      }
                    >
                      <X className="size-3.5" />
                    </Button>
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}

        <AutosizeTextarea
          value={viewModel.value}
          minHeight={compact ? 44 : 54}
          maxHeight={256}
          aria-label={messages.composer.message}
          placeholder={viewModel.placeholder}
          className={cn(
            "min-w-0 w-full resize-none overflow-y-auto rounded-none border-0 bg-transparent px-0 py-0 text-sm leading-relaxed text-foreground shadow-none focus-visible:ring-0 focus-visible:ring-offset-0",
            classNames?.textarea,
          )}
          onChange={(event) =>
            onIntent?.({ type: "change-value", value: event.target.value })
          }
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              submit();
            }
          }}
        />

        <div
          className={cn(
            "agent-workbench-composer__toolbar mt-2 flex min-w-0 max-w-full items-center",
            compact ? "min-h-7 gap-1.5" : "min-h-8 gap-2",
            isNarrow ? 'flex-wrap' : 'flex-nowrap',
            classNames?.toolbar,
          )}
        >
          <div
            data-agent-workbench-composer-controls
            className={cn(
              "agent-workbench-composer__controls flex min-w-0 max-w-full flex-1 items-center gap-1.5",
              isNarrow ? 'w-full flex-wrap' : 'flex-nowrap overflow-x-auto overscroll-x-contain pb-1',
            )}
          >
            {viewModel.capabilities.attachments ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={iconButtonClass}
                  aria-label={messages.composer.attach}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="size-4" />
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);
                    if (files.length > 0)
                      onIntent?.({ type: "request-attachments", files });
                    event.target.value = "";
                  }}
                />
              </>
            ) : null}
            {inputActions.length > 0 ? (
              <Tabs
                value={selectedInputActionId}
                onValueChange={(actionId) =>
                  onIntent?.({ type: "select-input-action", actionId })
                }
              >
                <TabsList
                  className={cn(
                    "shrink-0 gap-0.5 rounded-md bg-muted/60 p-0.5",
                    compact ? "h-7" : "h-8",
                  )}
                >
                  {inputActions.map((action) => {
                    const ActionIcon =
                      action.icon === "image" ? ImageIcon : FileText;
                    return (
                      <TabsTrigger
                        key={action.id}
                        value={action.id}
                        className={cn(
                          "min-w-0 gap-1.5 px-2.5 text-xs text-foreground/75 shadow-none hover:text-foreground data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none",
                          compact ? "h-6" : "h-7",
                        )}
                      >
                        {action.icon ? (
                          <ActionIcon className="size-3.5" />
                        ) : null}
                        <span className="max-w-32 truncate">
                          {action.label}
                        </span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                {inputActions.map((action) => (
                  <TabsContent
                    key={action.id}
                    value={action.id}
                    className="hidden"
                  />
                ))}
              </Tabs>
            ) : null}
            {viewModel.capabilities.voiceInput ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={iconButtonClass}
                aria-label={messages.composer.voice}
                onClick={() => onIntent?.({ type: "request-voice-input" })}
              >
                <Mic className="size-4" />
              </Button>
            ) : null}
            {viewModel.capabilities.webSearch ? (
              <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap px-1 text-xs font-medium text-foreground">
                {messages.composer.webSearch}
                <Switch
                  checked={viewModel.webSearchEnabled}
                  onCheckedChange={(enabled) =>
                    onIntent?.({ type: "toggle-web-search", enabled })
                  }
                />
              </label>
            ) : null}
            {modeSelectionEnabled ? (
              <Tabs
                value={viewModel.mode}
                onValueChange={(mode) =>
                  onIntent?.({
                    type: "change-mode",
                    mode: mode as "chat" | "work",
                  })
                }
              >
                <TabsList
                  aria-label={messages.composer.chatMode}
                  className={cn(
                    "shrink-0 gap-0.5 rounded-md bg-muted/60 p-0.5",
                    compact ? "h-7" : "h-8",
                  )}
                >
                  {(["chat", "work"] as const).map((mode) => (
                    <TabsTrigger
                      key={mode}
                      value={mode}
                      className={cn(
                        "min-w-0 px-2.5 text-xs text-foreground/75 shadow-none hover:text-foreground data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none",
                        compact ? "h-6" : "h-7",
                      )}
                    >
                      {messages.modes[mode]}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {(["chat", "work"] as const).map((mode) => (
                  <TabsContent key={mode} value={mode} className="hidden" />
                ))}
              </Tabs>
            ) : null}
            {viewModel.capabilities.modelSelection ? (
              <ComposerSelect
                value={selectedModelValue}
                label={messages.composer.model}
                placeholder={messages.composer.noModels}
                className="w-48 min-w-36"
                options={visibleModelOptions.map((option) => ({
                  value: modelOptionValue(option),
                  label: `${option.label} · ${messages.modes[option.mode]}`,
                  disabled: option.disabled,
                }))}
                onValueChange={(value) => {
                  const option = visibleModelOptions.find(
                    (item) => modelOptionValue(item) === value,
                  );
                  if (option)
                    onIntent?.({
                      type: "select-model",
                      modelId: option.id,
                      mode: option.mode,
                    });
                }}
              />
            ) : null}
            {viewModel.capabilities.reasoning && viewModel.reasoning ? (
              <ComposerSelect
                value={viewModel.reasoning.value}
                label={messages.composer.reasoning}
                className="w-32 min-w-24"
                options={viewModel.reasoning.options.map((value) => ({
                  value,
                  label: messages.composer.reasoningOptions[value],
                }))}
                onValueChange={(value) =>
                  onIntent?.({
                    type: "change-reasoning",
                    value: value as "low" | "medium" | "high" | "xhigh",
                  })
                }
              />
            ) : null}
            {viewModel.capabilities.permissions &&
            viewModel.permissionProfile ? (
              <ComposerSelect
                value={viewModel.permissionProfile}
                label={messages.composer.permissions}
                className="w-40 min-w-32"
                options={(
                  ["approval-required", "auto-approve", "full-access"] as const
                ).map((value) => ({
                  value,
                  label: messages.composer.permissionOptions[value],
                }))}
                onValueChange={(value) =>
                  onIntent?.({
                    type: "change-permission",
                    value: value as AgentSessionPermissionProfile,
                  })
                }
              />
            ) : null}
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            {showSubmitButton ? (
              <Button
                type="submit"
                size="icon"
                className={cn(
                  iconButtonClass,
                  "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground disabled:bg-primary/40 disabled:!opacity-100",
                )}
                aria-label={
                  isBusy && viewModel.capabilities.queue
                    ? messages.composer.queue
                    : messages.composer.send
                }
                disabled={!canSubmit}
              >
                <Send className="size-4 !text-white [&_path]:!stroke-white" />
              </Button>
            ) : null}
            {isBusy && !canSubmit && viewModel.capabilities.stop ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={iconButtonClass}
                aria-label={
                  isStopping ? messages.run.stopping : messages.composer.stop
                }
                disabled={isStopping}
                onClick={() => onIntent?.({ type: "stop" })}
              >
                <Square className="size-3 fill-current" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {viewModel.status === "error" && viewModel.error ? (
        <p role="alert" className="text-sm text-destructive-text">
          {viewModel.error}
        </p>
      ) : null}
    </form>
    </fieldset>
  );
}
